import React, {
  createContext, useCallback, useContext, useState,
} from 'react';
import PropTypes from 'prop-types';
import { QueryVal } from 'Helpers/Query';

export const FORCE_FOCUS_MODE_QUERY_PARAM = 'forceFocusMode';
const isForcingFocusMode = () => QueryVal(FORCE_FOCUS_MODE_QUERY_PARAM) === 'true';
const useCallbackUnlessForcedFocusMode = (fn, deps) => useCallback((...args) => {
  if (!isForcingFocusMode()) {
    fn(...args);
  }
}, deps);

const defaultState = {
  isFocusedLayout: isForcingFocusMode(),
  isNavigationOpen: true,
  navigationStayOpen: false,
  isFooterVisible: true,
  hideBottomNavigation: false,
  refreshInvestmentAccounts: false,
  isNavigationVisible: !isForcingFocusMode(),
  showMobileMenuFrom: null,
};

const LayoutContext = createContext(defaultState);

const LayoutContextProvider = ({ children }) => {
  const [isFocusedLayout, _setFocusedLayout] = useState(defaultState.isFocusedLayout);
  const setFocusedLayout = useCallbackUnlessForcedFocusMode(_setFocusedLayout, []);

  const [isNavigationOpen, setNavigationOpen] = useState(defaultState.isNavigationOpen);
  const [navigationStayOpen, setNavigationStayOpen] = useState(defaultState.navigationStayOpen);

  const [isFooterVisible, setFooterVisibility] = useState(defaultState.isFooterVisible);
  const [isNavigationVisible, _setNavigationVisible] = useState(defaultState.isNavigationVisible);
  const setNavigationVisible = useCallbackUnlessForcedFocusMode(_setNavigationVisible, []);

  const [hideBottomNavigation, setHideBottomNavigation] = useState(defaultState.hideBottomNavigation);

  const [showMobileMenuFrom, setShowMobileMenuFrom] = useState(defaultState.showMobileMenuFrom);

  const focusedLayout = useCallbackUnlessForcedFocusMode(() => {
    setFocusedLayout(true);
    setNavigationOpen(false);
    setNavigationStayOpen(false);
  }, [setFocusedLayout, setNavigationOpen, setNavigationStayOpen]);
  const unfocusedLayout = useCallbackUnlessForcedFocusMode(() => {
    setFocusedLayout(false);
  }, [setFocusedLayout, setNavigationOpen, setNavigationStayOpen]);

  return (
    <LayoutContext.Provider
      value={{
        isFocusedLayout,
        focusedLayout,
        unfocusedLayout,
        setFocusedLayout,
        showMobileMenuFrom,
        setShowMobileMenuFrom,
        isNavigationOpen,
        navigationStayOpen,
        setNavigationOpen,
        setNavigationStayOpen,
        isFooterVisible,
        setFooterVisibility,
        hideBottomNavigation,
        setHideBottomNavigation,
        setNavigationVisible,
        isNavigationVisible,
      }}
    >
      {children}
    </LayoutContext.Provider>
  );
};

export const useLayoutContext = () => {
  const context = useContext(LayoutContext);
  if (!context) throw new Error('useLayoutContext must be called from LayoutContext.Provider');
  return context;
};

LayoutContextProvider.propTypes = {
  children: PropTypes.any.isRequired,
};

export default LayoutContextProvider;

export const MOBILE_MENU_FROM_BOTTOM = 'bottom';
export const MOBILE_MENU_FROM_LEFT = 'left';
